<script setup lang="ts">
import { X as XIcon } from '@lucide/vue'
import { ref, computed, watch, nextTick } from 'vue'

import InvitationAcceptanceForm from '@/components/auth/InvitationAcceptanceForm.vue'
import LoginForm from '@/components/auth/LoginForm.vue'
import PasswordResetConfirmForm from '@/components/auth/PasswordResetConfirmForm.vue'
import PasswordResetForm from '@/components/auth/PasswordResetForm.vue'
import ProfileEditForm from '@/components/auth/ProfileEditForm.vue'
import SignupForm from '@/components/auth/SignupForm.vue'
import { useEventListener } from '@/composables/useEventListener'
import { logService, LOG_CATEGORIES } from '@/services/logService'

export interface Props {
  isOpen: boolean
  initialView?:
    | 'login'
    | 'signup'
    | 'reset'
    | 'reset-confirm'
    | 'profile-edit'
    | 'invitation-acceptance'
  resetToken?: string
  initialInviteCode?: string
  invitationToken?: string
  invitationError?: string
  invitationLoading?: boolean
  invitationInfo?: { inviter: string; workspaceName: string; userExists: boolean }
}

interface Emits {
  close: []
  authSuccess: []
  invitationSuccess: [
    data: { password: string | undefined; profileName: string; workspaceClientId?: string },
  ]
  invitationError: [message: string]
}

const props = withDefaults(defineProps<Props>(), {
  initialView: 'login',
})

const emit = defineEmits<Emits>()

const currentView = ref<
  'login' | 'signup' | 'reset' | 'reset-confirm' | 'profile-edit' | 'invitation-acceptance'
>(props.initialView)
const resetEmail = ref('')

const modalRef = ref<HTMLElement | null>(null)
const previousFocusedElement = ref<HTMLElement | null>(null)
const closeButtonRef = ref<HTMLButtonElement | null>(null)

const modalTitle = computed(() => {
  switch (currentView.value) {
    case 'login':
      return 'Log in'
    case 'signup':
      return 'Create Account'
    case 'reset':
      return 'Reset Password'
    case 'reset-confirm':
      return 'Set New Password'
    case 'profile-edit':
      return 'Edit Profile'
    case 'invitation-acceptance':
      return 'Welcome to Routecamp'
    default:
      return 'Authentication'
  }
})

// Switch between views
const switchToLogin = () => {
  currentView.value = 'login'
}

const switchToSignup = () => {
  currentView.value = 'signup'
}

const switchToReset = (email = '') => {
  resetEmail.value = email
  currentView.value = 'reset'
}

// Handle successful authentication
const handleAuthSuccess = () => {
  emit('authSuccess')
  emit('close')
}

// Handle successful invitation acceptance
const handleInvitationSuccess = (data: {
  password: string | undefined
  profileName: string
  workspaceClientId?: string
}) => {
  emit('invitationSuccess', data)
}

// Handle invitation error
const handleInvitationError = (message: string) => {
  emit('invitationError', message)
}

// Handle modal close
const closeModal = () => {
  emit('close')
}

// Handle escape key
const handleEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.isOpen) {
    closeModal()
  }
}

// Handle tab trapping for keyboard navigation
const handleTabTrap = (event: KeyboardEvent) => {
  if (event.key !== 'Tab') return

  const focusableElements = modalRef.value?.querySelectorAll(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )

  if (!focusableElements || focusableElements.length === 0) return

  const firstElement = focusableElements[0] as HTMLElement
  const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault()
    lastElement.focus()
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault()
    firstElement.focus()
  }
}

// Handle backdrop click (more specific than document click)
const handleBackdropClick = (event: MouseEvent) => {
  // Only close if clicking directly on the backdrop (not on child elements)
  if (event.target === event.currentTarget) {
    closeModal()
  }
}

// Watcher for isOpen prop to manage focus and listeners
watch(
  () => props.isOpen,
  (newValue, oldValue) => {
    if (newValue && !oldValue) {
      // Store previously focused element
      previousFocusedElement.value = document.activeElement as HTMLElement

      // Reset view when modal opens
      if (props.initialView === 'reset-confirm' && props.resetToken) {
        currentView.value = 'reset-confirm'
      } else {
        currentView.value = props.initialView
      }

      // Focus management
      nextTick(() => {
        if (closeButtonRef.value) {
          closeButtonRef.value.focus()
        } else if (modalRef.value) {
          modalRef.value.focus()
        }
      })
    } else if (!newValue && oldValue) {
      // Restore focus to previously focused element
      if (previousFocusedElement.value) {
        nextTick(() => {
          try {
            previousFocusedElement.value?.focus()
          } catch (error) {
            logService.warn(LOG_CATEGORIES.UI, 'Failed to restore focus', { error })
          }
          previousFocusedElement.value = null
        })
      }
    }
  },
  { immediate: true },
)

// Watcher for initialView prop to update current view when props change
watch(
  () => props.initialView,
  (newView) => {
    if (props.isOpen) {
      // Only update if modal is open
      if (newView === 'reset-confirm' && props.resetToken) {
        currentView.value = 'reset-confirm'
      } else {
        currentView.value = newView
      }
    }
  },
)

// Use Vue composable for event listener management
useEventListener(document, 'keydown', handleEscape)
useEventListener(document, 'keydown', handleTabTrap)
</script>

<template>
  <!-- Modal overlay -->
  <div
    v-if="isOpen"
    :class="[
      // Positioning & Layout
      'fixed',
      'inset-0',
      'z-110',
      'flex',
      'items-center',
      'justify-center',
      // Visual
      'backdrop-blur-sm',
      'bg-gray-800/70',
    ]"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="`modal-title-${currentView}`"
    :aria-describedby="`modal-description-${currentView}`"
    @click="handleBackdropClick"
  >
    <!-- Screen reader description -->
    <div :id="`modal-description-${currentView}`" class="sr-only">
      Authentication dialog for
      {{
        currentView === 'login'
          ? 'signing in'
          : currentView === 'signup'
            ? 'creating an account'
            : currentView === 'reset'
              ? 'resetting password'
              : currentView === 'reset-confirm'
                ? 'setting new password'
                : currentView === 'profile-edit'
                  ? 'editing profile'
                  : 'accepting invitation'
      }}
    </div>

    <!-- Modal content -->
    <div
      ref="modalRef"
      :class="[
        // Positioning & Sizing
        'relative',
        'w-full',
        'max-w-md',
        'mx-4',
        // Constrain overall modal height and allow internal scrolling (matches BaseModal):
        // with the Android keyboard open the resized viewport can be shorter than the form.
        'max-h-[85vh]',
        'flex',
        'flex-col',
        // Visual
        'bg-white',
        'rounded-lg',
        'shadow-xl',
      ]"
      tabindex="-1"
      @click.stop
    >
      <!-- Modal header -->
      <div
        :class="[
          // Layout
          'flex',
          'items-center',
          'justify-between',
          // Spacing
          'p-6',
          // Border
          'border-b',
          'border-gray-200',
        ]"
      >
        <h2 :id="`modal-title-${currentView}`" class="text-xl font-semibold text-gray-900">
          {{ modalTitle }}
        </h2>
        <button
          ref="closeButtonRef"
          @click="closeModal"
          :class="[
            // Sizing
            'p-2',
            // Typography & Visual
            'text-gray-400',
            'rounded-full',
            // Interactive
            'hover:text-gray-600',
            'hover:bg-gray-100',
            'hover:cursor-pointer',
            'hover:scale-110',
            'hover:rotate-90',
            'transition-all',
            'duration-200',
            'ease-out',
            'active:scale-95',
            // Focus
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
            'focus:ring-offset-2',
          ]"
          aria-label="Close authentication dialog"
        >
          <XIcon class="w-5 h-5 transition-transform duration-200 ease-out" />
        </button>
      </div>

      <!-- Modal body -->
      <div
        :class="[
          // Spacing
          'p-6',
          // Scroll inside the modal when the (keyboard-resized) viewport is shorter than the form
          'flex-1',
          'overflow-y-auto',
        ]"
      >
        <!-- Login Form -->
        <LoginForm
          v-if="currentView === 'login'"
          @success="handleAuthSuccess"
          @switch-to-signup="switchToSignup"
          @switch-to-reset="switchToReset"
        />

        <!-- Signup Form -->
        <SignupForm
          v-else-if="currentView === 'signup'"
          :initial-invite-code="initialInviteCode"
          @success="handleAuthSuccess"
          @switch-to-login="switchToLogin"
        />

        <!-- Password Reset Form -->
        <PasswordResetForm
          v-else-if="currentView === 'reset'"
          :initial-email="resetEmail"
          @success="switchToLogin"
          @switch-to-login="switchToLogin"
        />

        <!-- Password Reset Confirm Form -->
        <PasswordResetConfirmForm
          v-else-if="currentView === 'reset-confirm' && resetToken"
          :token="resetToken"
          @success="switchToLogin"
          @switch-to-login="switchToLogin"
        />

        <!-- Profile Edit Form -->
        <ProfileEditForm
          v-else-if="currentView === 'profile-edit'"
          @success="handleAuthSuccess"
          @close="closeModal"
        />

        <!-- Invitation Acceptance Form -->
        <InvitationAcceptanceForm
          v-else-if="currentView === 'invitation-acceptance'"
          :invitation-token="invitationToken"
          :error="invitationError"
          :is-loading="invitationLoading"
          :invitation-info="invitationInfo"
          @success="handleInvitationSuccess"
          @error="handleInvitationError"
        />
      </div>
    </div>
  </div>
</template>
